import { HTMLAttributes, RefObject, useEffect, useMemo, useRef } from 'react';
import { AriaListbox } from './AriaListbox';

type AriaListboxConfig = {
  id: string;
  enabled?: boolean;
  handleFocusChange?: (isFocused: boolean, element: HTMLElement) => void;
};

export const useAriaListboxPattern = <T extends HTMLElement = HTMLElement>({
  id,
  enabled = true,
  handleFocusChange,
}: AriaListboxConfig) => {
  const listboxRef = useRef<T>(null);
  const ariaListboxRef = useRef<AriaListbox | null>(null);

  const listboxProps = useMemo<HTMLAttributes<T> & { ref: RefObject<T | null> }>(
    () => ({
      ref: listboxRef,
      'aria-labelledby': id,
      tabIndex: 0,
    }),
    [id],
  );

  useEffect(() => {
    if (!listboxRef.current || ariaListboxRef.current || !enabled) return;

    const listboxEl = listboxRef.current;
    const ariaListbox = new AriaListbox(listboxEl, { handleFocusChange });
    ariaListboxRef.current = ariaListbox;

    return ariaListbox.unregisterEvents.bind(ariaListbox);
  }, [enabled, handleFocusChange]);

  return {
    listboxProps,
    ariaListbox: ariaListboxRef.current,
  };
};
